import { TOptions } from "i18next";
import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { useMedia } from "react-use";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { actionRequestToJoin, IActionRequestToJoinParams, IRequestToJoinCompany } from "@/data/companies";
import { formatFullName } from "@/data/persons";
import { TUserRole } from "@/data/session";
import { IUserPartial } from "@/data/users";
import { Flex, IDialogModal, inDialogModal } from "@/eworkitComponents";
import { CloseButton } from "@/views/components/CloseButton";
import { StyledModal } from "@/views/components/Modal";
import { useToasts } from "@/views/components/Toast/useToasts";
import { InvitationRolePickerBox } from "@/views/Invitations/InvitationRolePickerBox";

import "./_request-to-join-modal.scss";

interface IRequestToJoinDialogProps extends IDialogModal {
  requestToJoin: Pick<IRequestToJoinCompany, "id" | "status">;
  user: IUserPartial;
  onSuccess?: () => void;
  existingRole?: TUserRole | null;
}

const RequestToJoinDialog: FC<IRequestToJoinDialogProps> = props => {
  const { addToast } = useToasts();

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const { t } = useTranslation(["session", "common"]);

  const { requestToJoin, onClose, onSuccess } = props;
  const [selectedRole, setSelectedRole] = useState<TUserRole | null>(null);

  const handleRequest = async (action: IActionRequestToJoinParams["action"], successMessage: string) => {
    try {
      await actionRequestToJoin({ action, requestToJoinId: requestToJoin.id, role: selectedRole });

      if (onSuccess) {
        onSuccess();
      }

      addToast({
        text: successMessage,
        type: "success",
      });
    } catch (response) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "danger",
      });
    } finally {
      onClose();
    }
  };

  const acceptRequest = () => handleRequest("accept", t("session:COMPANY.REQUEST_TO_JOIN_MODAL.ACCEPTED_MESSAGE"));
  const rejectRequest = () => handleRequest("reject", t("session:COMPANY.REQUEST_TO_JOIN_MODAL.REJECTED_MESSAGE"));

  const baseClassName = "request-to-join-modal";

  const getText = (key: string, options?: string | TOptions) => {
    const statusKey = requestToJoin.status;
    return t(`session:COMPANY.REQUEST_TO_JOIN_MODAL.${statusKey}.${key}`, options);
  };

  return (
    <StyledModal className={baseClassName}>
      <Flex direction="column" margin={["xl"]} innerMargin="lg">
        <Flex justify="space-between">
          <Header size="md" color="black" weight="bold" text={getText("HEADER")} />
          <CloseButton onClick={onClose} testName="close" />
        </Flex>

        <Flex>
          <Text size="xl" hjSuppress>
            <Trans
              i18nKey={`session:COMPANY.REQUEST_TO_JOIN_MODAL.${requestToJoin.status}.TEXT`}
              values={{ value: formatFullName(props.user), role: props.existingRole ?? "CONSULTANT".toLowerCase() }}>
              <Text weight={"bold"} size={"xl"} />
            </Trans>
          </Text>
        </Flex>

        <Flex>
          <Text size={"lg"} color={"grey1"}>
            {t("session:COMPANY.REQUEST_TO_JOIN_MODAL.PENDING.CHOOSE_PERMISSION_LEVEL")}
          </Text>
        </Flex>

        <Flex direction={isSmallScreen ? "column" : "row"} innerMargin="md">
          <InvitationRolePickerBox
            header={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.MEMBER")}
            description={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.MEMBER_DESCRIPTION")}
            onClick={() => setSelectedRole("CONSULTANT")}
            icon="user"
            selected={selectedRole === "CONSULTANT"}
          />
          <InvitationRolePickerBox
            header={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.MANAGER")}
            description={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.MANAGER_DESCRIPTION")}
            onClick={() => setSelectedRole("MANAGER")}
            icon="user_settings"
            selected={selectedRole === "MANAGER"}
          />
          <InvitationRolePickerBox
            header={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.ADMIN")}
            description={t("CONSULTANT_PROFILES.INVITATION.ROLE_PICKER.ADMIN_DESCRIPTION")}
            onClick={() => setSelectedRole("ADMIN")}
            icon="user_hierarchy"
            selected={selectedRole === "ADMIN"}
          />
        </Flex>

        <Flex innerMargin="md" justify="flex-end">
          {requestToJoin.status === "PENDING" && (
            <>
              <Button variant="secondary" onClick={rejectRequest} size="lg" testName="reject">
                {t("session:COMPANY.REQUEST_TO_JOIN_MODAL.BUTTON_REJECT")}
              </Button>
              <Button variant="primary" size="lg" onClick={acceptRequest} testName="accept" disabled={!selectedRole}>
                {t("session:COMPANY.REQUEST_TO_JOIN_MODAL.BUTTON_ACCEPT")}
              </Button>
            </>
          )}

          {requestToJoin.status !== "PENDING" && (
            <Button variant="primary" onClick={props.onClose} size="lg" testName="continue">
              {t("session:COMPANY.REQUEST_TO_JOIN_MODAL.BUTTON_CONTINUE")}
            </Button>
          )}
        </Flex>
      </Flex>
    </StyledModal>
  );
};

const RequestToJoinDialogModalComponent = inDialogModal(RequestToJoinDialog);

interface IRequestToJoinDialogModalProps extends IRequestToJoinDialogProps {}

export const RequestToJoinDialogModal: FC<IRequestToJoinDialogModalProps> = props => {
  return <RequestToJoinDialogModalComponent size="md" {...props} />;
};
